import React from 'react';
import { Link } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Star } from 'lucide-react';

export default function TestimonialsPage() {
  const testimonials = [
    {
      name: "Rajesh Kumar",
      procedure: "ACL Reconstruction",
      rating: 5,
      text: "Dr. Sujit Kumar performed my ACL surgery and the results have been excellent. He explained everything clearly and the physiotherapy team helped me get back to playing cricket within 8 months. Highly recommend!",
      initials: "RK",
    },
    {
      name: "Priya Sharma",
      procedure: "Shoulder Arthroscopy",
      rating: 5,
      text: "I had been suffering from shoulder pain for over a year. Dr. Kumar diagnosed a rotator cuff tear and performed arthroscopic surgery. The recovery was much faster than I expected and I'm now pain-free.",
      initials: "PS",
    },
    {
      name: "Anil Reddy",
      procedure: "Knee Replacement",
      rating: 5,
      text: "After years of knee pain from arthritis, I finally decided to get a knee replacement. Dr. Kumar and his team were wonderful throughout the process. I can now walk without pain and even climb stairs easily.",
      initials: "AR",
    },
    {
      name: "Sneha Patel",
      procedure: "Sports Injury Treatment",
      rating: 5,
      text: "As a badminton player, I suffered an ankle injury that I thought would end my career. Dr. Kumar's treatment and the rehabilitation program got me back on court stronger than before. Thank you!",
      initials: "SP",
    },
    {
      name: "Vikram Singh",
      procedure: "Fracture Treatment",
      rating: 5,
      text: "I had a complex wrist fracture from a bike accident. Dr. Kumar performed surgery to fix it with plates and screws. The healing was perfect and I've regained full function. Very grateful for his expertise.",
      initials: "VS",
    },
    {
      name: "Lakshmi Devi",
      procedure: "Hip Replacement",
      rating: 5,
      text: "I was hesitant about hip replacement surgery, but Dr. Kumar explained everything patiently and addressed all my concerns. The surgery went smoothly and my quality of life has improved dramatically.",
      initials: "LD",
    },
    {
      name: "Karthik Rao",
      procedure: "Meniscus Repair",
      rating: 5,
      text: "Dr. Kumar repaired my torn meniscus arthroscopically. The small incisions healed quickly and I was back to running within 6 weeks. Excellent care from consultation to recovery.",
      initials: "KR",
    },
    {
      name: "Meena Iyer",
      procedure: "Frozen Shoulder Treatment",
      rating: 5,
      text: "I couldn't lift my arm due to frozen shoulder. Dr. Kumar's treatment plan with physiotherapy and injections worked wonders. I now have full range of motion back. Highly professional clinic.",
      initials: "MI",
    },
    {
      name: "Suresh Babu",
      procedure: "Ankle Ligament Reconstruction",
      rating: 5,
      text: "After multiple ankle sprains, I needed ligament reconstruction. Dr. Kumar's surgical skill and the post-op care were outstanding. My ankle is now stable and I'm back to playing football.",
      initials: "SB",
    },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary to-primary/90 text-primary-foreground py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-2xl">
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl mb-4 font-playfair">
              Patient Testimonials
            </h1>
            <p className="text-lg text-primary-foreground/90 leading-relaxed">
              Read what our patients say about their treatment experience and recovery
            </p>
          </div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              We're proud to have helped hundreds of patients recover from injuries and return to their active
              lifestyles. Here are some of their stories.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <Avatar className="h-12 w-12 bg-secondary text-secondary-foreground">
                      <AvatarFallback>{testimonial.initials}</AvatarFallback>
                    </Avatar>
                    <div>
                      <h3 className="font-semibold">{testimonial.name}</h3>
                      <p className="text-sm text-muted-foreground">{testimonial.procedure}</p>
                    </div>
                  </div>

                  <div className="flex gap-1 mb-4">
                    {Array.from({ length: testimonial.rating }).map((_, i) => (
                      <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>

                  <p className="text-sm text-foreground leading-relaxed">{testimonial.text}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-muted/30">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4 font-playfair">
            Ready to Start Your Recovery Journey?
          </h2>
          <p className="text-lg mb-8 text-muted-foreground max-w-2xl mx-auto">
            Join our many satisfied patients and experience expert orthopaedic care.
          </p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <Button size="lg" asChild>
              <Link to="/book-appointment">Book Appointment</Link>
            </Button>
            <Button variant="outline" size="lg" asChild>
              <Link to="/contact">Contact Us</Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}